import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Download, FileSpreadsheet, Calendar, ShieldCheck, Lock, Zap, Briefcase } from "lucide-react";
import QuarterlyFilingCenter from "@/components/QuarterlyFilingCenter";
import { Form940Card, StateReturnCard, CarrierRatesCard, DepositRemindersCard } from "@/components/FilingCards";
import { YearEndCard, SubmitterCard } from "@/components/YearEndCards";
import Form1099Ops from "@/components/Form1099Ops";
import { CorrectionsCard, StateWageFileCard, JanuaryChecklistCard } from "@/components/CorrectionCards";

const CHART_TICK_STYLE = { fontFamily: "IBM Plex Mono", fontSize: 10 };
const CHART_TOOLTIP_STYLE = { border: "2px solid #0A0A0A", borderRadius: 0, fontFamily: "IBM Plex Mono" };
const CHART_LEGEND_STYLE = { fontFamily: "IBM Plex Mono", fontSize: 10 };

const QUARTERS = [1, 2, 3, 4];

export default function Reports() {
  const [s, setS] = useState(null);
  const [pro, setPro] = useState(null);
  const [busy, setBusy] = useState(false);
  const [empLiab, setEmpLiab] = useState(null);
  const [range, setRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
    year: String(new Date().getFullYear()),
  });
  const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
  const [quarter, setQuarter] = useState(currentQuarter);

  const load = useCallback(async () => {
    const bs = await api.get("/billing/status").catch(() => ({ data: { pro: { active: false } } }));
    setPro(bs.data?.pro || { active: false });
    // Only pull the (Pro-gated) summary if the caller actually has access — a
    // non-Pro admin should still see the locked preview page, not a 402 flood.
    if (bs.data?.pro?.active) {
      try {
        const rs = await api.get("/reports/summary");
        setS(rs.data);
      } catch { setS(null); }
    } else {
      // Placeholder shape so the preview renders with $0s under the lock overlay
      setS({ ytd: { gross: 0, net_pay: 0, federal_tax: 0, social_security: 0, medicare: 0, state_tax: 0 } });
    }
    // Employer Liability — Enterprise-Grade summary. Silent-fail so free tier
    // still renders the locked preview without noisy toasts.
    try {
      const el = await api.get(`/reports/employer-liability?year=${new Date().getFullYear()}`);
      setEmpLiab(el.data);
    } catch { setEmpLiab(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadQuarterlyPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/reports/quarterly-tax.pdf?year=${range.year}&quarter=${quarter}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `quarterly-tax-Q${quarter}-${range.year}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Q${quarter} ${range.year} report downloaded`);
    } catch (e) {
      const detail = formatApiError(e);
      toast.error(detail.includes("Professional") ? "Professional plan required — see Billing" : detail);
    } finally { setBusy(false); }
  };

  const downloadQuarterlyCsv = async (format) => {
    setBusy(true);
    try {
      const res = await api.get(`/reports/quarterly-tax.csv?year=${range.year}&quarter=${quarter}&format=${format}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `quarterly-${format}-Q${quarter}-${range.year}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`${format === "irs_941" ? "IRS 941" : "NY NYS-45"} CSV downloaded`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="p-10 font-mono">Loading…</div>;

  const totalTaxes = (s.ytd.federal_tax || 0) + (s.ytd.social_security || 0) + (s.ytd.medicare || 0) + (s.ytd.state_tax || 0);
  const payrollUrl = `${API}/reports/payroll.csv?start=${range.start}&end=${range.end}`;
  const taxUrl = `${API}/reports/tax-liability.csv?year=${range.year}`;
  const timeUrl = `${API}/reports/time-entries.csv?start=${range.start}&end=${range.end}`;
  const tvpUrl = `${API}/reports/time-vs-payroll.csv?start=${range.start}&end=${range.end}`;
  const proActive = pro && pro.active;

  // Non-Pro admins get a locked SNEAK PEEK: the whole page renders behind a
  // frosted-glass overlay with a big "Start Pro Trial" CTA. This turns the
  // gate into a conversion point instead of a hard bounce.
  const LockedOverlay = () => (
    <div className="fixed inset-0 z-30 bg-white/70 backdrop-blur-md flex items-center justify-center pointer-events-none" data-testid="reports-lock-overlay">
      <div className="hard-card max-w-lg w-full mx-6 p-8 text-center pointer-events-auto bg-white border-4 border-ink shadow-hard-lg">
        <div className="mx-auto w-14 h-14 bg-warn border-2 border-ink flex items-center justify-center mb-4"><Lock className="w-7 h-7 text-ink" /></div>
        <div className="overline mb-2">§ SNEAK PEEK</div>
        <h2 className="font-heading font-black text-3xl tracking-tight mb-3">Reports are a Professional feature</h2>
        <p className="font-mono text-xs text-muted-foreground leading-relaxed mb-6">Payroll CSVs, quarterly tax PDFs, time-vs-payroll variance reports, and the full W-2 batch export live behind the Pro tier. Preview what's here, then upgrade to unlock exports.</p>
        <div className="grid grid-cols-2 gap-3">
          <Link
            to="/app/billing"
            data-testid="start-pro-trial"
            className="hard-btn py-3 px-4 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2"
          ><Zap className="w-4 h-4" /> Start Pro Trial</Link>
          <Link
            to="/app/billing"
            data-testid="learn-more-pro"
            className="border-2 border-ink py-3 px-4 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 hover:bg-secondary"
          ><ShieldCheck className="w-4 h-4" /> See Pricing</Link>
        </div>
        <div className="mt-6 font-mono text-[10px] text-muted-foreground">Ex-subscriber? You get 15 days of grace access — keep exporting even after cancellation.</div>
      </div>
    </div>
  );

  return (
    <div className="p-10 relative">
      {!proActive && <LockedOverlay />}
      <div className="overline mb-2">§ REPORTS</div>
      <h1 className="font-heading font-black text-5xl tracking-tight mb-8">Payroll &amp; Tax Liability</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        <Card label="YTD Gross" val={s.ytd.gross} />
        <Card label="YTD Employer + Employee Taxes" val={totalTaxes} color="text-bad" />
        <Card label="YTD Net Disbursed" val={s.ytd.net_pay} color="text-good" />
      </div>

      {/* EMPLOYER LIABILITY · Enterprise-Grade */}
      {empLiab && (
        <div className="hard-card p-6 mb-8" data-testid="employer-liability-card">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="overline mb-1 flex items-center gap-2"><Briefcase className="w-4 h-4" /> § EMPLOYER LIABILITY · {empLiab.year}{empLiab.quarter ? ` Q${empLiab.quarter}` : " YTD"}</div>
              <h2 className="font-heading font-black text-2xl">Total employer cost above wages</h2>
            </div>
            <div className="font-mono text-[10px] px-2 py-1 border-2 border-warn text-warn font-black uppercase tracking-widest">Enterprise-Grade</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Card label="Gross Payroll" val={empLiab.gross_payroll} />
            <Card label="WC Premium" val={empLiab.wc_premium_total} color="text-warn" />
            <Card label="Employer SS Match" val={empLiab.employer_ss_match} color="text-bad" />
            <Card label="Employer Medicare" val={empLiab.employer_medicare_match} color="text-bad" />
            <Card label="Total Liability" val={empLiab.total_employer_liability} color="text-good" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="overline mb-2">Quarterly Stack — Employer Cost Breakdown</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={[
                  { q: `Q${empLiab.quarter || "YTD"}`, "WC": empLiab.wc_premium_total, "FICA": empLiab.employer_ss_match + empLiab.employer_medicare_match, "Garnishments": empLiab.garnishments_remitted },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="q" tick={CHART_TICK_STYLE} />
                  <YAxis tick={CHART_TICK_STYLE} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                  <Bar dataKey="WC" stackId="a" fill="#e6b000" />
                  <Bar dataKey="FICA" stackId="a" fill="#e1301a" />
                  <Bar dataKey="Garnishments" stackId="a" fill="#2b8f60" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-3 items-start">
              <div className="border-2 border-ink p-3">
                <div className="overline mb-2">WC by Class Code</div>
                {Object.keys(empLiab.wc_by_class || {}).length === 0 ? (
                  <div className="font-mono text-[11px] text-muted-foreground italic">No WC premiums recorded — add class code + rate to a Worksite.</div>
                ) : Object.entries(empLiab.wc_by_class).map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono text-xs py-1 border-b border-ink/20 last:border-b-0">
                    <span>Class {k}</span><span>{fmtMoney(v)}</span>
                  </div>
                ))}
              </div>
              <div className="border-2 border-ink p-3">
                <div className="overline mb-2">Garnishments Remitted</div>
                {Object.keys(empLiab.garnishments_by_order || {}).length === 0 ? (
                  <div className="font-mono text-[11px] text-muted-foreground italic">No court-order deductions this period.</div>
                ) : Object.entries(empLiab.garnishments_by_order).map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono text-xs py-1 border-b border-ink/20 last:border-b-0">
                    <span className="truncate max-w-[10rem]" title={k}>{k}</span><span>{fmtMoney(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button onClick={() => downloadQuarterlyCsv("irs_941")} disabled={busy || !proActive} data-testid="download-irs-941-csv" className="border-2 border-ink px-4 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 hover:bg-ink hover:text-white disabled:opacity-40">
              <Download className="w-4 h-4" /> IRS 941 CSV · Q{quarter} {range.year}
            </button>
            <button onClick={() => downloadQuarterlyCsv("ny_nys45")} disabled={busy || !proActive} data-testid="download-ny-nys45-csv" className="border-2 border-ink px-4 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 hover:bg-ink hover:text-white disabled:opacity-40">
              <Download className="w-4 h-4" /> NY NYS-45 CSV · Q{quarter} {range.year}
            </button>
          </div>
        </div>
      )}

      <QuarterlyFilingCenter year={range.year} enabled={proActive} />
      <YearEndCard year={range.year} enabled={proActive} />
      <div className="mb-8"><Form1099Ops year={range.year} /></div>
      <JanuaryChecklistCard year={range.year} enabled={proActive} />
      <CorrectionsCard year={range.year} enabled={proActive} />
      <StateWageFileCard year={range.year} enabled={proActive} />
      <SubmitterCard />
      <DepositRemindersCard year={range.year} />
      <StateReturnCard year={range.year} enabled={proActive} />
      <Form940Card year={range.year} enabled={proActive} />
      <CarrierRatesCard />

      {/* COMPLIANCE — Quarterly Tax Summary */}      <div className="hard-card p-6 mb-8 relative" data-testid="quarterly-tax-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> § COMPLIANCE REPORTING</div>
            <div className="font-heading font-black text-2xl tracking-tight">Quarterly Tax Summary</div>
            <div className="font-mono text-xs text-muted-foreground mt-1 max-w-lg">Generate a downloadable PDF that aggregates gross wages, federal/state withholding, SS &amp; Medicare, and net disbursed for the selected quarter — plus a per-employee breakdown. Perfect for filing 941s.</div>
          </div>
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${proActive ? "bg-good text-white" : "bg-warn text-ink"}`} data-testid="pro-status-pill">
            {proActive ? "Professional" : "Pro required"}
          </span>
        </div>
        <div className="mt-5 grid grid-cols-12 gap-3 items-end">
          <div className="col-span-3">
            <div className="overline mb-2 text-[10px]">Year</div>
            <input data-testid="quarterly-year" type="text" value={range.year} onChange={(e) => setRange({ ...range, year: e.target.value })} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          </div>
          <div className="col-span-3">
            <div className="overline mb-2 text-[10px]">Quarter</div>
            <select data-testid="quarterly-quarter" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm">
              {QUARTERS.map((q) => (
                <option key={q} value={q}
                  label={`Q${q}${q === currentQuarter && Number(range.year) === new Date().getFullYear() ? " · Current" : ""}`} />
              ))}
            </select>
          </div>
          <div className="col-span-6">
            <button
              data-testid="quarterly-download-btn"
              onClick={downloadQuarterlyPdf}
              disabled={busy || !proActive}
              className="w-full hard-btn py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {!proActive && <Lock className="w-3.5 h-3.5" />}
              {proActive ? <Download className="w-4 h-4" /> : null}
              {busy ? "Generating…" : `Download Q${quarter} ${range.year} Tax Summary PDF`}
            </button>
          </div>
        </div>
      </div>

      {/* CSV EXPORTS */}
      <div className="hard-card p-6 mb-8">
        <div className="overline mb-4 flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> CSV Exports</div>
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-4">
            <div className="overline mb-2 text-[10px]">Start Date</div>
            <input data-testid="report-start" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          </div>
          <div className="col-span-4">
            <div className="overline mb-2 text-[10px]">End Date</div>
            <input data-testid="report-end" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          </div>
          <div className="col-span-2">
            <div className="overline mb-2 text-[10px]">Tax Year</div>
            <input data-testid="report-year" type="text" value={range.year} onChange={(e) => setRange({ ...range, year: e.target.value })} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          </div>
          <div className="col-span-2 text-right text-xs font-mono text-muted-foreground">
            <Calendar className="w-4 h-4 inline mr-1" /> range applies to Payroll &amp; Time exports
          </div>

          <div className="col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
            <a data-testid="download-payroll-csv" href={payrollUrl} target="_blank" rel="noreferrer" className="hard-btn py-3 font-mono uppercase text-xs tracking-widest text-center inline-flex items-center justify-center gap-2">
              <Download className="w-4 h-4" /> Payroll CSV
            </a>
            <a data-testid="download-tax-csv" href={taxUrl} target="_blank" rel="noreferrer" className="border-2 border-ink py-3 font-mono uppercase text-xs tracking-widest text-center inline-flex items-center justify-center gap-2 hover:bg-secondary">
              <Download className="w-4 h-4" /> Tax Liability CSV
            </a>
            <a data-testid="download-time-report-csv" href={timeUrl} target="_blank" rel="noreferrer" className="border-2 border-ink py-3 font-mono uppercase text-xs tracking-widest text-center inline-flex items-center justify-center gap-2 hover:bg-secondary">
              <Download className="w-4 h-4" /> Time Entries CSV
            </a>
            <a data-testid="download-time-vs-payroll-csv" href={tvpUrl} target="_blank" rel="noreferrer" className="border-2 border-ink py-3 font-mono uppercase text-xs tracking-widest text-center inline-flex items-center justify-center gap-2 hover:bg-secondary" title="Actual clocked hours vs what payroll paid — spot missed OT or overpaid weeks">
              <Download className="w-4 h-4" /> Time vs Payroll CSV
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="hard-card p-6">
          <div className="overline mb-4">TAX BREAKDOWN · YTD</div>
          <table className="w-full font-mono text-sm">
            <tbody>
              <Row label="Federal Income Tax" val={s.ytd.federal_tax} />
              <Row label="Social Security" val={s.ytd.social_security} />
              <Row label="Medicare" val={s.ytd.medicare} />
              <Row label="State Tax" val={s.ytd.state_tax} />
              <tr className="font-black"><td className="pt-3 border-t-2 border-ink">TOTAL TAX LIABILITY</td><td className="pt-3 border-t-2 border-ink text-right">{fmtMoney(totalTaxes)}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="hard-card p-6">
          <div className="overline mb-4">RUN HISTORY</div>
          <div className="h-64 min-h-[256px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.monthly}>
                <CartesianGrid stroke="#0A0A0A" strokeDasharray="2 2" opacity={0.15} />
                <XAxis dataKey="label" tick={CHART_TICK_STYLE} />
                <YAxis tick={CHART_TICK_STYLE} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                <Bar dataKey="gross" fill="#0A0A0A" name="Gross" />
                <Bar dataKey="taxes" fill="#E1301A" name="Taxes" />
                <Bar dataKey="net" fill="#00A050" name="Net" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ label, val, color = "" }) {
  return (
    <div className="hard-card p-6">
      <div className="overline mb-2">{label}</div>
      <div className={`font-mono font-black text-3xl ${color}`}>{fmtMoney(val)}</div>
    </div>
  );
}
function Row({ label, val }) {
  return <tr><td className="py-1.5">{label}</td><td className="text-right py-1.5">{fmtMoney(val)}</td></tr>;
}
